import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ConnectionService } from './connection.service';

export type MpdPlaybackState = 'play' | 'pause' | 'stop';

export interface MpdEndpoint {
  host: string;
  port: number;
}

export interface MpdStatus {
  connected: true;
  endpoint: MpdEndpoint;
  serverVersion: string;
  state: MpdPlaybackState;
  volume: number | null;
  elapsed: number | null;
  duration: number | null;
  playlistLength: number;
  repeat: boolean;
  random: boolean;
  single: boolean;
  consume: boolean;
  song: {
    title: string;
    artist: string;
    album: string;
    file: string;
  } | null;
}

export interface MpdAlbum {
  id: string;
  name: string;
  relativePath: string;
  trackCount: number;
  hasCover: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class MPDService {
  constructor(
    private http: HttpClient,
    private connectionService: ConnectionService
  ) {}

  getStatus(): Observable<MpdStatus> {
    return this.http.get<MpdStatus>(
      `${this.connectionService.address}/mpd-service/status`,
      { headers: this.getHeaders() }
    );
  }

  play(): Observable<MpdStatus> {
    return this.http.post<MpdStatus>(
      `${this.connectionService.address}/mpd-service/play`,
      {},
      { headers: this.getHeaders() }
    );
  }

  pause(): Observable<MpdStatus> {
    return this.http.post<MpdStatus>(
      `${this.connectionService.address}/mpd-service/pause`,
      {},
      { headers: this.getHeaders() }
    );
  }

  toggle(): Observable<MpdStatus> {
    return this.http.post<MpdStatus>(
      `${this.connectionService.address}/mpd-service/toggle`,
      {},
      { headers: this.getHeaders() }
    );
  }

  previous(): Observable<MpdStatus> {
    return this.http.post<MpdStatus>(
      `${this.connectionService.address}/mpd-service/previous`,
      {},
      { headers: this.getHeaders() }
    );
  }

  next(): Observable<MpdStatus> {
    return this.http.post<MpdStatus>(
      `${this.connectionService.address}/mpd-service/next`,
      {},
      { headers: this.getHeaders() }
    );
  }

  getAlbums(): Observable<MpdAlbum[]> {
    return this.http.get<MpdAlbum[]>(
      `${this.connectionService.address}/mpd-service/albums`,
      { headers: this.getHeaders() }
    );
  }

  getAlbumCover(albumId: string): Observable<Blob> {
    return this.http.get(
      `${this.connectionService.address}/mpd-service/albums/${encodeURIComponent(albumId)}/cover`,
      {
        headers: this.getHeaders(),
        responseType: 'blob'
      }
    );
  }

  playAlbum(albumId: string): Observable<MpdStatus> {
    return this.http.post<MpdStatus>(
      `${this.connectionService.address}/mpd-service/albums/play`,
      { albumId },
      { headers: this.getHeaders() }
    );
  }

  private getHeaders(): HttpHeaders {
    const token = this.connectionService.activeConnection?.token
      || localStorage.getItem('token')
      || '';

    return token ? new HttpHeaders({ authtoken: token }) : new HttpHeaders();
  }
}
